import { useEffect, useRef } from "react";
import { useWorkspaceSelection } from "./useWorkspaceSelection";

/**
 * Page-level selection keyboard: Escape clears, Enter opens a single
 * selection.
 *
 * Every value the handler reads — selection, the open dispatcher — churns
 * on each click. Listing them as effect deps would tear down and
 * re-register the window keydown listener on every keystroke-worthy state
 * change, so the listener mounts once and reads the latest handler from a
 * ref refreshed every render.
 */
export function useSelectionKeyboard(openSelectedId: (id: string) => void) {
  const selection = useWorkspaceSelection();

  const keydownHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keydownHandlerRef.current = (e: KeyboardEvent) => {
    // Don't hijack keystrokes that belong to a focused editor — Escape
    // should close the search box / rename dialog / etc., and Enter should
    // submit the focused input, not open a selected item.
    const target = e.target as HTMLElement | null;
    if (target) {
      const tag = target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target.isContentEditable
      ) {
        return;
      }
    }

    if (e.key === "Escape") {
      selection.clear();
    }
    if (e.key === "Enter" && selection.selectionCount === 1) {
      openSelectedId(Array.from(selection.selectedIds)[0]);
    }
  };

  useEffect(() => {
    const listener = (e: KeyboardEvent) => keydownHandlerRef.current(e);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);
}
